import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyQuantityChange,
  buildNewProductRequest,
  clearProductChange,
  countProductChanges,
  createEmptyDraft,
  getOriginalDateTime,
  getProductChange,
  hasAnyChanges,
  hasDateTimeChanges,
  hasProductEdits
} from '../../lib/changeRequestDraft';

export default function useRequestChangesDraft(proposal) {
  const [draft, setDraft] = useState(() => createEmptyDraft(proposal));
  const proposalRef = useRef(proposal);
  proposalRef.current = proposal;

  useEffect(() => {
    setDraft(createEmptyDraft(proposalRef.current));
  }, [proposal.projectNumber, proposal.version]);

  const resetDraft = useCallback(() => {
    setDraft(createEmptyDraft(proposalRef.current));
  }, []);

  const setQuantity = useCallback((payload) => {
    setDraft((current) => applyQuantityChange(current, payload));
  }, []);

  const requestRemoval = useCallback((payload) => {
    setDraft((current) => applyQuantityChange(current, { ...payload, newQuantity: 0, removed: true }));
  }, []);

  const undoProductChange = useCallback((sectionIdx, productIdx) => {
    setDraft((current) => clearProductChange(current, sectionIdx, productIdx));
  }, []);

  const addNewProductRequest = useCallback((product) => {
    const next = buildNewProductRequest(product);
    if (!next.name || !next.section) return;
    setDraft((current) => ({
      ...current,
      newProducts: [...current.newProducts, next]
    }));
  }, []);

  const removeNewProductRequest = useCallback((index) => {
    setDraft((current) => ({
      ...current,
      newProducts: current.newProducts.filter((_, i) => i !== index)
    }));
  }, []);

  const setDateTime = useCallback((field, value) => {
    setDraft((current) => ({
      ...current,
      dateTimeChanges: {
        ...current.dateTimeChanges,
        [field]: value
      }
    }));
  }, []);

  const setDateTimeFields = useCallback((fields) => {
    setDraft((current) => ({
      ...current,
      dateTimeChanges: {
        ...current.dateTimeChanges,
        ...fields
      }
    }));
  }, []);

  const resetDateTime = useCallback(() => {
    setDraft((current) => ({
      ...current,
      dateTimeChanges: getOriginalDateTime(proposalRef.current)
    }));
  }, []);

  const setMiscNotes = useCallback((value) => {
    setDraft((current) => ({
      ...current,
      miscNotes: value
    }));
  }, []);

  const productChangeCount = useMemo(() => countProductChanges(draft), [draft]);
  const productEdits = useMemo(() => hasProductEdits(draft), [draft]);
  const dateTimeEdits = useMemo(() => hasDateTimeChanges(draft, proposal), [draft, proposal]);
  const anyChanges = useMemo(() => hasAnyChanges(draft, proposal), [draft, proposal]);

  return {
    draft,
    resetDraft,
    setQuantity,
    requestRemoval,
    undoProductChange,
    addNewProductRequest,
    removeNewProductRequest,
    setDateTime,
    setDateTimeFields,
    resetDateTime,
    setMiscNotes,
    getProductChange: (sectionIdx, productIdx) => getProductChange(draft, sectionIdx, productIdx),
    productChangeCount,
    hasProductEdits: productEdits,
    hasDateTimeChanges: dateTimeEdits,
    hasAnyChanges: anyChanges
  };
}
